import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TbPill  } from "react-icons/tb";
import LoadingModal from "../../components/Modals/LoadingModal/LoadingModal";
import LoadingPage from "../Questionnaire/VariantB/LoadingPage";
import LoginModal from "../../components/Modals/LoginModal/LoginModal";
import ServerError from "../../components/ServerError/ServerError";
import HeaderController from "../../components/Headers/HeaderController";
import AnimatedPopup from "../../components/AnimatedPopup/AnimatedPopup";
import ExpandableSection from "../ChooseTreatmentCarePlus/components/ExpandableSection";
import { TrustBadgeSection } from "../../components/Modals/SideMenu/components/TrustBadgeSection";
import { useAppContext } from "../../providers/AppProvider";
import { translate } from "../../utils/translator";
import { PLAN_STEP_HISTORY_KEY, rewriteCarePlusHistoryEntry, useChooseTreatmentCarePlus } from "../ChooseTreatmentCarePlus/useChooseTreatmentCarePlus";
import { CatalogSettingContext } from "../ChooseTreatmentVariants/chooseTreatmentContexts";
import Klarna from "../../icons/KlarnaLogo.svg";
import Visa from "../../icons/Visa.svg";
import Maestro from "../../icons/Maestro.svg";
import GPay from "../../icons/GPay.svg";
import ApplePay from "../../icons/ApplePay.svg";
import PayPal from "../../icons/PayPal.svg";

import { CAT, GRADIENT, TEXT_GRADIENT, HERO_GRADIENT } from "./constants";
import { buildImageUrl, intakeFrequency, usageCadence, contentBrand, AVG_WEIGHT_LOSS, medicationHeroImageClassName } from "./helpers";
import { CarePlusMedication } from "../ChooseTreatmentCarePlus/types";
import CarePlusHeaderBar from "../CarePlusShared/CarePlusHeaderBar";
import HelpBlock from "./components/HelpBlock";
import BenefitRow, { IconWeightLoss, IconBodyWeight, IconLabs } from "./components/BenefitRow";
import DoseDropdown from "./components/DoseDropdown";
import ExploreOptionsCard from "./components/ExploreOptionsCard";
import ModalOptionRow from "./components/ModalOptionRow";
import ComparisonTable from "./components/ComparisonTable";
import PriceValue from "./components/PriceValue";
import ChoosePlanCarePlusNew from "../ChoosePlanCarePlusNew/ChoosePlanCarePlusNew";

const InjectionIcon = () => (
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 10 10" fill="none">
  <g clipPath="url(#clip0_5086_94095)">
    <path d="M7.49479 0.828125L9.16145 2.49479M7.07812 2.91146L8.32812 1.66146M7.91145 3.74479L3.61979 8.03646C3.20312 8.45312 2.57812 8.45312 2.20312 8.03646L1.95312 7.78646C1.53645 7.36979 1.53645 6.74479 1.95312 6.36979L6.24479 2.07812M3.74479 4.57812L5.41145 6.24479M2.07812 7.91146L0.828125 9.16146M5.82812 1.66146L8.32812 4.16146" stroke="#616975" strokeLinecap="round" strokeLinejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_5086_94095">
      <rect width="10" height="10" fill="white"/>
    </clipPath>
  </defs>
</svg>
);

const TreatmentIcon = ({ medication }: { medication: CarePlusMedication }) => {
  const frequency = intakeFrequency(medication, (key, fallback) => fallback);
  const isInjection = /injection/i.test(frequency);

  return isInjection ? <InjectionIcon /> : <TbPill size={16} className="text-[#616975]" />;
};

const ChooseTreatmentCarePlusNew = () => {
  const { country, language, category } = useParams() as { country: string; language: string; category: string };
  const { headerVersion } = useAppContext();
  const navigate = useNavigate();
  const [showDosageHelp, setShowDosageHelp] = useState(false);
  const [showMoreOpen, setShowMoreOpen] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const oauthReturnPath = `${window.location.pathname.replace(/^\//, "")}${window.location.search}`;

  // HL-12294: a reload (or an SSO return) lands on the /choose_plan URL, which redirects here with
  // reopenChoosePlan — reopen the plan step for the product preselected from the cart, once.
  const location = useLocation();
  const reopenChoosePlan = Boolean((location.state as { reopenChoosePlan?: boolean } | null)?.reopenChoosePlan);
  const catalogSettings = useContext(CatalogSettingContext) as { ct_preselected_catalog_id?: number | null };

  const {
    loading,
    error,
    medications,
    recommended,
    alternatives,
    selectedDosage,
    setSelectedDosageId,
    selectRecommended,
    continueWithMedication,
    openPlanStep,
    goToPlan,
    setGoToPlan,
    submitting,
    showLoginModal,
    closeLoginModal,
    onLoginSuccess,
  } = useChooseTreatmentCarePlus({
    preferCart: reopenChoosePlan,
    ctPreselectedCatalogId: catalogSettings?.ct_preselected_catalog_id,
  });

  const reopenedChoosePlan = useRef(false);
  useEffect(() => {
    if (!reopenChoosePlan || reopenedChoosePlan.current || loading || !recommended || !selectedDosage) return;
    if (recommended.is_pre_orderable) return; // pre-orders skip Choose Plan
    reopenedChoosePlan.current = true;
    rewriteCarePlusHistoryEntry(`/choose_plan/${country}/${language}/${category}${window.location.search}`);
    setGoToPlan(true);
  }, [reopenChoosePlan, loading, recommended, selectedDosage, country, language, category, setGoToPlan]);

  // HL-12294 (Figma 7204:48343): the CTA sits under the selected product's benefits, between it and the
  // other options. Whenever that slot is not fully on screen the same button is pinned to the bottom.
  const [ctaSlot, setCtaSlot] = useState<HTMLDivElement | null>(null);
  const [isCtaInSlot, setIsCtaInSlot] = useState(false);
  useEffect(() => {
    if (!ctaSlot || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setIsCtaInSlot(entry.isIntersecting), { threshold: 1 });
    observer.observe(ctaSlot);
    return () => observer.disconnect();
  }, [ctaSlot]);

  // HL-12777: browser Back / Forward across the entry Continue pushed. /choose_plan renders this same
  // page, so neither remounts it: Back closes the step and keeps the picked medication + dose on screen,
  // Forward reopens it for the selection on screen. A pre-order skips Choose Plan, so it stays on the list.
  // The listener is added once and reads this render's values through the ref: the router re-renders
  // the page from its own popstate listener first, and a listener re-added by that render would be
  // skipped for the event already being dispatched.
  const onPopStateRef = useRef<() => void>(() => {});
  onPopStateRef.current = () => {
    if (!window.location.pathname.startsWith("/choose_plan/")) {
      setGoToPlan(false);
      return;
    }
    if (goToPlan) return;
    if (recommended && !recommended.is_pre_orderable) {
      openPlanStep();
      return;
    }
    navigate(
      { pathname: `/select_treatment/${country}/${language}/${category}`, search: window.location.search },
      { replace: true },
    );
  };
  useEffect(() => {
    const onPopState = () => onPopStateRef.current();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const backToTreatment = () => {
    // The arrow does what browser Back does when Continue pushed the plan step's entry. A reopened
    // step (reload, SSO return) has no entry of its own, so it only swaps the view back.
    if (window.history.state?.[PLAN_STEP_HISTORY_KEY]) {
      window.history.back();
      return;
    }
    rewriteCarePlusHistoryEntry(`/select_treatment/${country}/${language}/${category}${window.location.search}`);
    setGoToPlan(false);
  };

  if (error) {
    return (
      <>
        <HeaderController showSteps={false} stage={"Questionnaire"} headerVersion={headerVersion} />
        <ServerError status={500} />
      </>
    );
  }

  // HL-12229: keep the questionnaire's "finding the right solution" loader up until the treatment page is ready.
  // HL-12357: V3 centres the loader's heading + bar on mobile.
  if (loading) return <LoadingPage language={language} centerContent />;
  if (submitting) return <LoadingModal />;

  if (!recommended) {
    return (
      <>
        <HeaderController showSteps={false} stage={"Questionnaire"} headerVersion={headerVersion} />
        <ServerError status={404} />
      </>
    );
  }

  if (goToPlan) {
    return (
      <ChoosePlanCarePlusNew
        onBack={backToTreatment}
        dosages={recommended.dosages}
        initialMedication={{
          title: recommended.title,
          imageSrc: buildImageUrl(recommended.focus_image ?? recommended.image) || "",
          quantityStr: [selectedDosage?.dosage_str, selectedDosage?.quantity_title]
            .filter(Boolean)
            .join(" · "),
          dosage_str: selectedDosage?.dosage_str,
          quantity_title: selectedDosage?.quantity_title,
          quantity: selectedDosage?.quantity,
          item_price: selectedDosage?.price ?? 0,
          official_price: selectedDosage?.official_price ?? selectedDosage?.price ?? 0,
          catalog_id: recommended.catalog_id,
          product_item_id: selectedDosage?.product_item_id ?? 0,
          product_in_stock: selectedDosage?.in_stock ?? false,
          is_pre_orderable: recommended.is_pre_orderable ?? false,
        }}
      />
    );
  }

  const canContinue = Boolean(selectedDosage && Number.isFinite(selectedDosage.price) && selectedDosage.price > 0);
  const imageUrl = buildImageUrl(recommended.focus_image ?? recommended.image);
  const weightLossBrand = contentBrand(recommended.title);
  const weightLossPct = AVG_WEIGHT_LOSS[weightLossBrand]?.pct?.replace("-", "") || (language === "de" ? "14,9 %" : "14.9%");
  const featuredExploreCards = (() => {
    const byBrand = (brand: string) => alternatives.find((m) => contentBrand(m.title) === brand);
    const selectedBrand = contentBrand(recommended.title);
    const trio = ["wegovy", "wegovy_tablet", "mounjaro"];
    const featuredBrands = trio.includes(selectedBrand)
      ? trio.filter((brand) => brand !== selectedBrand)
      : ["wegovy_tablet", "mounjaro"];
    return featuredBrands.map(byBrand).filter((m): m is CarePlusMedication => Boolean(m));
  })();
  const shownCards = featuredExploreCards.length ? featuredExploreCards : alternatives.slice(0, 2);
  const remainingCards = alternatives.filter((m) => !shownCards.some((s) => s.catalog_id === m.catalog_id));
  const t = (key: string, fallback: string) => translate(language, CAT, key) ?? fallback;

  const handleExploreSelect = (catalogId: number) => {
    selectRecommended(catalogId);
    setShowMoreOpen(false);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleModalSelect = (catalogId: number) => {
    setShowMoreOpen(false);
    window.setTimeout(() => {
      selectRecommended(catalogId);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  const faqSections = (() => {
    // FAQ copy keys are shared across Wegovy forms (pen/tablet) — unlike contentBrand (pricing/
    // comparison), don't split "wegovy_tablet" out here, there is no such key in translations.
    const brand = recommended.title.toLowerCase().includes("wegovy") ? "wegovy" : contentBrand(recommended.title);
    const content = (section: string) => (brand ? t(`medication_content/${brand}/${section}`, "") : "");
    // HL-12035 PM FAQ update: the five production sections, without "What's included in your plan".
    return [
      { key: "how_it_works", title: t("how_it_works", "How it works"), body: content("how_it_works") },
      { key: "how_to_take", title: t("how_to_take", "How to take"), body: content("how_to_take") },
      { key: "side_effects", title: t("side_effects", "Potential common side effects"), body: content("side_effects") },
      { key: "safety", title: t("safety", "Important safety information"), body: content("safety") },
    ].filter((s) => s.body);
  })();

  return (
    <>
      <div id="wl3_treatment_page" ref={topRef} className="relative min-h-screen bg-white pb-28">
        {/* Full-bleed hero. Gradient spans the viewport; content stays 520px. 80% layer like Choose Plan (Figma 5096:105010). */}
        <div
          className="absolute left-0 top-0 w-full h-[320px] opacity-80 md:h-[587px]"
          style={{ background: HERO_GRADIENT }}
        />

        <div className="relative z-10 mx-auto max-w-[520px]">
          {/* pt clears the absolute header bar so the image centers in the space below it. */}
          <div
            className="relative flex w-full items-center justify-center pt-[56px]"
            style={{ height: 257 }}
          >
            <CarePlusHeaderBar backId="wl3_treatment_back" onBack={() => navigate(-1)} t={t} />
            {imageUrl && (
              <img
                key={recommended.catalog_id}
                src={imageUrl}
                alt={recommended.title}
                className={medicationHeroImageClassName(recommended.title)}
              />
            )}
          </div>

          <div className="relative z-20 bg-white rounded-3xl px-6 pt-[23px]">
          {/* Title + frequency */}
          <div className="flex flex-col gap-2.5">
            <h1 className="text-[24px] font-medium leading-[32px] tracking-[-0.72px] text-[#08080a]">
              {recommended.title}
            </h1>
            <div className="flex items-center justify-between gap-[12px]">
              <div className="flex items-center gap-[7px]">
                <TreatmentIcon medication={recommended} />
                <p className="text-[14px] text-[#616975]">{usageCadence(recommended, t)}</p>
              </div>
              <PriceValue
                value={selectedDosage?.price ?? 0}
                suffix={` ${t("per_mo", "/mo")}`}
                className="shrink-0 font-poppins text-[18px] font-semibold leading-[21px] tracking-[-0.36px] text-[#616975]"
              />
            </div>
          </div>

          {/* Dose dropdown + need help */}
          <div className="mt-[20px] flex flex-col gap-[12px]">
            <DoseDropdown
              dosages={recommended.dosages}
              selectedId={selectedDosage?.product_item_id ?? null}
              onSelect={setSelectedDosageId}
              t={t}
            />
            <p className="w-4/5 text-balance text-[12px] leading-[17.5px] text-[#e88103] md:w-full">
              {t("doctor_reviews_dosage", "Your doctor will review your details and adjust the dosage if needed.")}
            </p>
            <button
              id="wl3_treatment_dosage_help"
              type="button"
              onClick={() => setShowDosageHelp(true)}
              className="w-fit cursor-pointer border-0 bg-transparent p-0 text-left text-[14px] text-[#61a333] underline"
            >
              {t("need_help", "Need help?")}
            </button>
          </div>

          {/* Benefit bullets */}
          <div className="mt-[20px] flex flex-col gap-[12px]">
            {([
              [IconWeightLoss, `${t("benefit_weight_loss", "Average weight loss benchmark:")} ${weightLossPct}*`],
              [IconBodyWeight, t("benefit_fuller", "Helps you feel fuller for longer")],
              [IconLabs, t("benefit_personalized", "Personalized dosing based on your needs")],
            ] as const).map(([Icon, text], i) => (
              <BenefitRow key={i} icon={<Icon />} text={text} />
            ))}
          </div>

          {/* CTA slot: keeps its height while the button is pinned, so nothing jumps. */}
          <div ref={setCtaSlot} className="mt-[32px] h-[60px]">
            <div className={isCtaInSlot ? "h-full" : "fixed bottom-0 left-0 right-0 z-40 mx-auto w-full max-w-[520px] px-4 pb-6"}>
              <button
                id="wl3_treatment_continue"
                type="button"
                disabled={!canContinue}
                onClick={continueWithMedication}
                className="flex cursor-pointer h-[60px] w-full items-center justify-center rounded-[12px] border-0 text-[16px] font-medium text-white drop-shadow-[0px_-8px_9px_rgba(8,8,10,0.1)] disabled:cursor-not-allowed disabled:bg-[#b5bdcc]"
                style={{ background: canContinue ? GRADIENT : undefined }}
              >
                {/* One inline box: as separate flex items the space before the product name collapses. */}
                <span>
                  {t("continue_cta", "Continue with")} <span className="font-semibold">{recommended.title}</span>
                </span>
              </button>
            </div>
          </div>

          {/* Explore other options */}
          {shownCards.length > 0 && (
            <div className="mt-[64px] flex flex-col items-center gap-[14px] rounded-[16px] bg-[#f5f6f7] py-[20px]">
              <p className="text-center text-[14px] font-medium text-[#f08e00]">
                {t("explore_other", "Explore other treatment options")}
              </p>
              <div className="flex w-[314px] flex-col gap-[12px]">
                {shownCards.map((med) => (
                  <ExploreOptionsCard key={med.catalog_id} medication={med} onSelect={() => handleExploreSelect(med.catalog_id)} t={t} />
                ))}
              </div>
              {remainingCards.length > 0 && (
                <button
                  id="wl3_treatment_show_more"
                  type="button"
                  onClick={() => setShowMoreOpen(true)}
                  className="cursor-pointer border-0 bg-transparent p-0 text-[14px] text-[#616975] underline"
                >
                  {t("show_more", "Show more")} +{remainingCards.length}
                </button>
              )}
            </div>
          )}

          {/* Trust badge */}
          <div className="mt-[24px]">
            <TrustBadgeSection className="bg-white w-fit mx-auto" language={language} country={country} trustedShopsData={{ overallMark: "4,81" }} />
          </div>

          {/* Comparison table */}
          {medications.length > 0 && (
            <div className="mt-[24px] flex flex-col gap-[12px]">
              <div className="text-center">
                <h2 className="text-[24px] font-medium leading-[32px] text-black">
                  {t("comparison_title", "Differences between weight loss treatments")}
                </h2>
                <p className="mt-[12px] text-[15px] text-[#616975]">
                  {t("comparison_subtitle", "Which treatment is most suitable depends on individual needs")}
                </p>
              </div>
              <ComparisonTable
                medications={["mounjaro", "wegovy", "wegovy_tablet"]
                  .map((brand) => medications.find((m) => contentBrand(m.title) === brand))
                  .filter((m): m is CarePlusMedication => Boolean(m))}
                t={t}
                language={language}
              />
            </div>
          )}

          {/* FAQ */}
          <div className="mt-6 rounded-2xl bg-[#f5f6f7] px-5 py-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <p className="text-[10px] text-[#616975]">{t("faq_eyebrow", "FREQUENTLY ASKED QUESTIONS")}</p>
              <div className="w-[278px] text-[24px] font-medium leading-[32px] text-black">
                <p>{t("faq_title", "Do you have any further questions? We have the answers")}</p>
              </div>
            </div>
            <div className="mt-10">
              {faqSections.map((s) => (
                <ExpandableSection key={s.key} id={`wl3_treatment_faq_${s.key}`} title={s.title} bodyHtml={s.body} />
              ))}
            </div>
          </div>

          {/* Payment methods */}
          <div className="mt-[50px] flex flex-col items-center gap-[10px] ">
            <p className="text-[16px] font-medium text-[#1c1917]">{t("accepted_payment_methods", "Accepted payment methods")}</p>
            {/* Logos shrink below 45px on narrow phones so the six of them fit instead of being cropped. */}
            <div className="flex w-full items-center justify-center gap-[10px] ">
              {[Klarna, Visa, Maestro, GPay, ApplePay, PayPal].map((src, i) => (
                <div key={i} className="flex min-w-0 max-w-[53px] flex-1 items-center justify-center bg-[#f5f6f7] rounded-md p-1 md:max-w-none md:flex-none">
                  <img src={src} alt="" className="h-[22px] w-full md:w-auto" />
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Show more modal */}
      <AnimatedPopup
        isVisible={showMoreOpen}
        onClose={() => setShowMoreOpen(false)}
        closeButtonId="wl3_treatment_show_more_popup_close"
        title=""
        animationDurationMs={250}
        closeOnEscape
      >
        <div id="wl3_treatment_show_more_popup" className="flex flex-col gap-1.5 -mt-4">
          <h2 className="text-[24px] font-medium leading-[1.3] text-[#08080a]">
            {t("treatment_options_modal_title", "Your treatment options")}
          </h2>
          <p className="text-[15px] leading-[1.3] text-[#616975]">
            {t("treatment_options_modal_subtitle", "Based on your information, these treatments are suitable for you.")}
          </p>
        </div>
        <div
          className="mt-6 flex max-h-[450px] flex-col gap-4 overflow-y-auto pr-1.5"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#f08e00 #fff7ea",
          }}
        >
          {shownCards.map((med) => (
            <ModalOptionRow key={med.catalog_id} medication={med} onSelect={() => handleModalSelect(med.catalog_id)} t={t} />
          ))}
          {remainingCards.map((med) => (
            <ModalOptionRow key={med.catalog_id} medication={med} onSelect={() => handleModalSelect(med.catalog_id)} t={t} />
          ))}
        </div>
      </AnimatedPopup>

      {/* Dosage help popup (HL-11991, Figma 7157-23937) */}
      <AnimatedPopup
        isVisible={showDosageHelp}
        onClose={() => setShowDosageHelp(false)}
        closeButtonId="wl3_treatment_dosage_help_popup_close"
        title=""
        closeOnEscape
        centered
        containerClassName="!max-w-[354px] !rounded-[16px]"
      >
        <h2 id="wl3_treatment_dosage_help_popup" className="mb-[24px] text-[21px] font-medium leading-[32px] text-black">
          {t("dosage_help_v3_how", "How to")}{" "}
          <span className="bg-clip-text text-transparent" style={{ backgroundImage: TEXT_GRADIENT }}>
            {t("dosage_help_v3_choose", "choose your dosage")}
          </span>
        </h2>
        <div className="flex flex-col gap-[20px]">
          {[
            ["dosage_help_v3_start_title", "dosage_help_v3_start_body", "Starting treatment", "We recommend starting on the lowest dose. The dose is then increased slowly as your body gets used to the medication."],
            ["dosage_help_v3_continue_title", "dosage_help_v3_continue_body", "Continuing treatment", "If you are taking this medication now, or took it in the last month, choose the dose you are currently taking."],
            ["dosage_help_v3_restart_title", "dosage_help_v3_restart_body", "Restarting treatment", "If your last dose was more than 2 months ago, it is best to restart on a low dose."],
          ].map(([titleKey, bodyKey, titleFallback, bodyFallback]) => (
            <HelpBlock
              key={titleKey}
              title={t(titleKey, titleFallback)}
              body={t(bodyKey, bodyFallback)}
            />
          ))}
        </div>
      </AnimatedPopup>

      {showLoginModal && (
        <LoginModal
          idPrefix="wl3_treatment_login"
          setStickyNotificationDetails={() => {}}
          closeModal={closeLoginModal}
          navigationCallback={onLoginSuccess}
          changeShowForgotPassword={() => {}}
          language={language}
          country={country}
          redirectPath={oauthReturnPath as any}
          cameFromPath={oauthReturnPath as any}
          registerHref={`/${country}/register?redirect=${encodeURIComponent(`/${oauthReturnPath}`)}` as any}
        />
      )}
    </>
  );
};

export default ChooseTreatmentCarePlusNew;

